import JSZip from 'jszip';
import countries from 'i18n-iso-countries';
import countryLanguage from 'i18n-iso-countries/langs/en.json';
import { saveAs } from 'file-saver';
import axios, { AxiosError } from 'axios';
import { MDSSnackbar } from '@marqvision/mds-v2';
import { CUSTOM_COUNTRY_NAME_LIST, RESERVED_COUNTRY_LIST } from '../constants';
import { REGEX_EMAIL } from './RegEx';

declare global {
  interface Navigator {
    msSaveBlob?: (blob: Blob, filename?: string) => boolean;
  }
}

//#region Manipulate array
export const toggleOnArray = <T extends string | number>(source: T[], element: T | T[]): T[] => {
  const set = new Set<T>(source);

  const arrayToToggle = element instanceof Array ? element : [element];

  const isSubset = isSubsetArray(source, arrayToToggle);

  arrayToToggle.forEach((element) => {
    if (isSubset && set.has(element)) {
      set.delete(element);
    } else {
      set.add(element);
    }
  });

  return [...set];
};

export const removeFromArray = <T>(source: T[], element: T | T[], comparator?: (item: T | T[]) => void): T[] => {
  const arr = [...source];
  const targets = Array.isArray(element) ? element : [element];

  targets.forEach((target) => {
    const removeIndex = arr.findIndex((item) => (comparator ? comparator(item) : item === target));

    if (removeIndex >= 0) {
      arr.splice(removeIndex, 1);
    }
  });

  return arr;
};

export const getIntersectionArray = (a: unknown[], b: unknown[]): unknown[] => {
  const set1 = new Set(a);
  const set2 = new Set(b);

  const intersection = [...set1].filter((element) => set2.has(element));

  return intersection;
};

export const isSubsetArray = <T extends string | number>(set: T[], subset: T[]): boolean =>
  subset.every((element) => set.includes(element));
//#endregion

//#region Download file
export const downloadFile = async (
  fileOrUrl: File | string,
  filename?: string,
  onError?: (error: AxiosError) => void
): Promise<void> => {
  const a = document.createElement('a');
  if (filename) {
    a.download = filename;
  }

  let url: string;
  if (fileOrUrl instanceof File) {
    url = window.URL.createObjectURL(fileOrUrl);
    a.download = fileOrUrl.name;
  } else {
    const blob = await axios.get(fileOrUrl, { responseType: 'blob' });
    if (blob.status < 300) {
      url = URL.createObjectURL(blob.data);
    } else {
      onError?.(blob.data);
      return;
    }
  }

  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.href = url;
  a.click();

  window.URL.revokeObjectURL(url);
};
export const downloadFileWithFilename = async (url: string, filename: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    fetch(url, { cache: 'no-cache' })
      .then((response) => response.blob())
      .then((blob) => {
        const blobURL = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobURL;
        if (filename && filename.length) a.download = filename;
        document.body.appendChild(a);
        a.click();
        resolve();
      })
      .catch((err) => reject(err));
  });
};

export const downloadBlob = (data: unknown, filename: string): void => {
  if (!data) return;

  const blob = new Blob(['\ufeff' + data], { type: 'text/csv;charset=utf-8;' });

  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, filename);

    return;
  }

  const lnk = document.createElement('a');
  const url = window.URL;
  let objectURL;

  lnk.download = filename || 'untitled';
  lnk.href = objectURL = url.createObjectURL(blob);
  lnk.dispatchEvent(new MouseEvent('click'));
  setTimeout(url.revokeObjectURL.bind(url, objectURL));
};

// note-@jamie: packages/brand-client/src/utils/index.tsx 의 파일과 같은 기능을 하는 함수
export const downloadResponseTypeBlobFile = (data: any, filename: string, contentType?: string): void => {
  const url = window.URL.createObjectURL(new Blob([data], { type: contentType }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
//#endregion

//#region Formatters
export const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
export const trimTag = (text: string) => text.replace(/<[a-z]+\s*\/>/, ' ');
//#endregion

//#region Getters
/**
 * 주어진 URL 문자열을 검사하여 유효한 형태로 반환합니다.
 * - 인코딩되지 않은 경우 encodeURI 적용
 * - 프로토콜(http, https) 또는 루트(/)로 시작하지 않으면 `//`를 앞에 붙여 반환
 */
export const getValidUrl = (url: string): string => {
  // 잘못된 쿼리 구조 (?&) 보정
  url = url.replace(/\?&/, '?');

  try {
    return new URL(url).toString();
  } catch {
    // 이미 인코딩된 문자열이 중복 인코딩되지 않도록 decode 후 encode 시도
    try {
      url = encodeURI(decodeURI(url));
    } catch {
      url = encodeURI(url);
    }

    if (/^(?:https?:)?\/\//i.test(url) || url.startsWith('/')) {
      return url;
    }

    return `//${url}`;
  }
};

export const getFileNameFromUrl = (url: string): string => url.split('/').pop()?.split('?')[0] || '';

export const getFileNameAndExtension = (fullFileName: string): [string, string] => {
  const [_, name = '', ext = ''] = Array.from(/(.*)(\.[a-zA-Z0-9]+$)/.exec(fullFileName) || []);

  return [name, ext];
};

/**
 * 주어진 URL에서 프로토콜(http:// 또는 https://)과 끝에 붙은 슬래시(/)를 제거합니다.
 */
export const removeUrlProtocol = (url: string): string => {
  const protocolAndTrailingSlashRegExp = /^(http|https):\/\/|\/$/g;
  return url.replace(protocolAndTrailingSlashRegExp, '');
};

// aliases
// - packages/mds/core/DropDown/@utils.ts:getRegExpByKeyword
export const getRegExpByKeyword = (keyword: string) => {
  return new RegExp(keyword.replace(/[.*+?^${}()\[\]\\]/g, '\\$&'), 'ig');
};

export const getUniqueURLs = (urlLike: string, urlList: string[]) => {
  const urls: string[] = [];

  urlLike.split('\n').forEach((url) => {
    // split by row (in google spreadsheet)
    if (!url.trim().length) return;
    if (urlList.includes(url)) return;

    urls.push(url);
  });

  return urls;
};

export const getCountryEnName = (countryCode?: string): string => {
  countries.registerLocale(countryLanguage);

  if (!countryCode) {
    return '';
  }

  if (Object.keys(RESERVED_COUNTRY_LIST).includes(countryCode)) {
    return RESERVED_COUNTRY_LIST[countryCode as keyof typeof RESERVED_COUNTRY_LIST];
  } else if (Object.keys(CUSTOM_COUNTRY_NAME_LIST).includes(countryCode)) {
    return CUSTOM_COUNTRY_NAME_LIST[countryCode as keyof typeof CUSTOM_COUNTRY_NAME_LIST];
  } else {
    return countries.getName(countryCode, 'en', { select: 'alias' }) || '';
  }
};

//#endregion

//#region Parsers
export function parseJwt<T>(rawToken: string): T | undefined {
  try {
    // 토큰을 '.' 기준으로 분리
    const base64Url = rawToken.split('.')[1];
    // base64를 디코딩하여 JSON 문자열로 변환
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    // JSON 객체로 반환
    return JSON.parse(jsonPayload) as T;
  } catch (e) {
    // 오류 처리
    console.error('Invalid JWT Token', e);
  }
}

export const base64ToArrayBuffer = (base64: string) => {
  const binaryString = window.atob(base64);
  const binaryLen = binaryString.length;
  const bytes = new Uint8Array(binaryLen);
  for (let i = 0; i < binaryLen; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};
//#endregion

//#region Feature Flag
const featureFlags = ['brand-client-8124-amplitude', 'opscc-ui-web-10377-model-sample-sheet'] as const;
type featureFlagKey = (typeof featureFlags)[number];
export const FeatureFlags = featureFlags.reduce(
  (acc, curr) => ({
    ...acc,
    [curr]: curr,
  }),
  {}
) as Record<featureFlagKey, string>;
//#endregion

//#region Validators
export const validatePassword = (value: string) => {
  const PasswordValidationRegex = /(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[@#$%^&+=!?`~*()_\-\[\]{}|;:'",.<>\/\\]).*$/;

  return PasswordValidationRegex.test(value);
};

export const validateEmail = (email: string) => email.length > 0 && REGEX_EMAIL.test(email);
//#endregion

export const pluralizeWord = (count: number, word: string) => {
  // 기본적으로 1 이 아닐때 s 만 붙임
  // 예외 단어가 생긴다면 라이브러리 추가
  return count !== 1 ? `${count.toLocaleString()} ${word}s` : `${count} ${word}`;
};

export const parseFixedPercent = (value?: number | string) => {
  if (typeof value === 'undefined') return '';
  const number = Number(value);

  return (Math.round(number * 10) / 10).toString();
};

/**
 * @param filename 최종적으로 만들어진 zip파일의 파일명
 * @param fileList 다운로드 받을 file 각각의 이름과 url을 담은 배열
 * @param setLoading zip파일이 다운로드 되는동안 사용될 isLoading state setter
 * @param setProgress zip파일이 다운로드 되는동안 사용자한테 노출해줄 progress 값 (0-100)
 * @returns
 */
export const saveZip = async (
  filename: string,
  fileList: { url: string; filename?: string | null }[],
  setLoading?: (loading: boolean) => void,
  setProgress?: (progress: number) => void
): Promise<{
  success: boolean;
  successfulDownloads: number;
  failedDownloads: number;
}> => {
  if (!fileList.length)
    return {
      success: false,
      successfulDownloads: 0,
      failedDownloads: 0,
    };

  setLoading?.(true);
  setProgress?.(0);

  // 몇개 다운로드 성공/실패했는지 기록하는 변수값
  // https://www.figma.com/design/Eak9mK2a70qzehJuAr8JLK/All.-Investigation.-Attaching-file?node-id=2069-32410&node-type=instance&m=dev
  let successfulDownloads = 0;
  let failedDownloads = 0;

  const fetchFiles = await Promise.allSettled(
    fileList.map(async ({ url }) => {
      try {
        const response = await fetch(url);
        if (response.status !== 200) throw new Error(response.statusText);
        successfulDownloads++;
        return await response.blob();
      } catch (error) {
        failedDownloads++;
        return Promise.reject(error);
      }
    })
  );

  const zip = new JSZip();
  fileList.forEach(({ url, filename }, index) => {
    const result = fetchFiles[index];
    if (result.status === 'fulfilled') {
      zip.file(filename || getFileNameFromUrl(url), result.value);
    } else if (process.env.NODE_ENV !== 'production') {
      console.group('🚨 File Download Failed');
      console.error('File URL:', url);
      console.error('Reason:', result.reason);
      console.groupEnd();
    }
  });

  try {
    const blob = await zip.generateAsync({ type: 'blob' }, (metadata) => {
      setProgress?.(Math.floor(metadata.percent));
    });
    setProgress?.(100);
    saveAs(blob, filename);

    return {
      success: true,
      successfulDownloads,
      failedDownloads,
    };
  } catch {
    return {
      success: false,
      successfulDownloads,
      failedDownloads,
    };
  } finally {
    setLoading?.(false);
  }
};

export * from './RegEx';
export * from '../commerce/utils';

export function jsonClone(obj: unknown) {
  if (!obj) {
    return obj;
  }

  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (e) {
    return obj;
  }
}

// aliases
// - packages/mds/core/FileUploader/@util.ts:jsonParse
export const jsonParse = (data?: string | null, defaultValue?: unknown) => {
  if (!data) return defaultValue;

  try {
    return JSON.parse(data);
  } catch {
    return defaultValue;
  }
};

//#region CvAssistant decide download
type CvAssistantFileInfo = {
  file_url?: string | null;
  seller_name: string;
};

//conversational assistant 페이지 내 스냅샷 파일 갯수에 따라 다른 다운로드 로직 적용
export const decideDownload = (clientName: string, _list: CvAssistantFileInfo[]) => {
  if (!_list.length) {
    MDSSnackbar({ type: 'error', title: 'No snapshot available' });
    return;
  }
  const list = _list.filter(hasFileUrl);

  if (list.length === 1) {
    const url = list[0].file_url;
    const [_, extension] = getFileNameAndExtension(getFileNameFromUrl(url));
    const filename = `${clientName}_CA_snapshot_${list[0].seller_name}.${extension}`;
    downloadFileWithFilename(url, filename);
    return;
  }

  const fileList = list.map(({ file_url, seller_name }) => {
    const [_, extension] = getFileNameAndExtension(getFileNameFromUrl(file_url));
    const filename = `${clientName}_CA_snapshot_${seller_name}.${extension}`;
    return { url: file_url, filename };
  });
  const filename = `${clientName}_CA_snapshot.zip`;
  saveZip(filename, fileList);
};

const hasFileUrl = (
  item: CvAssistantFileInfo
): item is {
  file_url: string;
  seller_name: string;
} => {
  return item.file_url !== null;
};
//#endregion

/**
 * 파일명이 20자 이상일 경우 20자까지만 표시하고 나머지는 ... 으로 대체
 * Chip 으로 파일을 표시하는 경우, 파일명이 길어지면 Chip 의 크기가 커지는 문제가 있어서 파일명을 줄여서 표시
 * @param filename
 */
export const sliceFilename = (filename: string) => {
  if (filename.length < 21) return filename;
  return `${filename.slice(0, 20)}...`;
};

/**
 * HTML 문자열에서 텍스트만 추출
 */
export const getTextFromHtml = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() || '';
};

export * from './monitorNetwork';
export * from './getMaintenanceContent';
export * from './country/getCountryEmojiLabel';
export * from './browser';
